/**
 * Sync to COA from the Scrubber review (operator order 2026-10-16).
 *
 * Once compliance accepts a universe, the Scrubber asks Core which Precision
 * COA monitoring rows that universe can score. Core matches on what was
 * stated, nothing more: the protocol and table, whose universe it is (the
 * entity picked before submitting decides internal COA or the FDR app's
 * COA), and the declared period. The dialog says what it found in one
 * sentence, lists the rows with their readiness, and syncs the chosen ones
 * through the same engine the COA row's own Sync button uses. Compliance
 * never has to leave the Scrubber. No model anywhere.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Notice, btn } from './ui';
import { fmtNum } from './api';

const API_ROOT = process.env.REACT_APP_API_URL || '';

async function coaJson(path, opts = {}) {
  const r = await fetch(`${API_ROOT}/api/coa${path}`, { credentials: 'include', ...opts });
  let data = null;
  try { data = await r.json(); } catch (_) { data = null; }
  if (!r.ok || (data && data.success === false)) {
    const err = new Error((data && (data.message || data.error)) || `Request failed (${r.status})`);
    err.status = r.status; err.data = data; throw err;
  }
  return data;
}

/** Leave for the COA row: the work plan opens on that activity. */
export function openInCoa(destination, activityId) {
  try { if (activityId) sessionStorage.setItem('coaOpenActivityId', String(activityId)); } catch (_) { /* private mode */ }
  window.location.assign((destination && destination.path) || '/app?app=coa');
}

const chip = (tone) => ({
  emerald: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
  amber: 'bg-amber-50 text-amber-800 ring-1 ring-amber-200',
  slate: 'bg-slate-100 text-slate-600 ring-1 ring-slate-200',
  rose: 'bg-rose-50 text-rose-700 ring-1 ring-rose-200',
  sky: 'bg-sky-50 text-sky-700 ring-1 ring-sky-200',
}[tone] || 'bg-slate-100 text-slate-600');

function Chip({ tone = 'slate', children, title }) {
  return <span title={title} className={`inline-block text-[11px] font-semibold rounded-md px-2 py-0.5 ${chip(tone)}`}>{children}</span>;
}

function CandidateRow({ c, checked, onToggle, snpe }) {
  const can = c.ready;
  return (
    <label className={`flex items-start gap-3 rounded-xl border px-4 py-3 transition ${checked ? 'border-orange-400 bg-orange-50' : can ? 'border-slate-200 bg-white hover:border-slate-300' : 'border-slate-100 bg-slate-50 opacity-80'} ${can ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
      <input type="checkbox" className="mt-1 accent-orange-600" checked={checked} disabled={!can} onChange={onToggle} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm font-semibold text-slate-800">
            {c.period_label}
            <span className="text-slate-400 font-normal"> · {String(c.measure_label || '').replace(/^SNPCC\s+—\s+/, '')}</span>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {c.synced_from_this && <Chip tone="emerald">Already scored from this file</Chip>}
            {!c.synced_from_this && c.synced_from !== null && c.synced_from !== undefined && <Chip tone="amber" title="Syncing replaces that score with this file's.">Scored from submission #{c.synced_from}</Chip>}
            {c.ready && !c.synced_from_this && <Chip tone="sky">Ready</Chip>}
            {!c.ready && <Chip tone="slate">Not ready</Chip>}
            {c.monitoring_status && c.monitoring_status !== 'awaiting' && c.monitoring_status !== 'scheduled' && <Chip tone={c.monitoring_status === 'met' ? 'emerald' : c.monitoring_status === 'missed' ? 'rose' : 'slate'}>{c.monitoring_status}</Chip>}
          </div>
        </div>
        <div className="text-[11px] text-slate-500 mt-0.5">
          {c.entity_name}{c.benchmark != null ? ` · benchmark ${c.benchmark}%` : ' · no benchmark'}{c.cadence && c.cadence !== 'monthly' ? ` · ${c.cadence}` : ''}
          {snpe && c.slice && c.slice.cases > 0 && <> · {fmtNum(c.slice.cases)} case{c.slice.cases === 1 ? '' : 's'} this month{c.slice.rate != null ? `, ${c.slice.rate}% timely` : ''}</>}
        </div>
        {!c.ready && c.ready_reason && <div className="text-[11px] text-amber-800 bg-amber-50 ring-1 ring-amber-200 rounded-md px-2 py-1 mt-1.5">{c.ready_reason}</div>}
      </div>
    </label>
  );
}

/**
 * @param submission  the Scrubber submission row (id, protocol, tableLabel, entityLabel, period)
 * @param afterApproval  true when the dialog opened itself right after the Accept decision
 * @param onSynced  called with the sync result so the list can refresh its marks
 */
export default function CoaSyncDialog({ open, submission: s, afterApproval = false, onClose, onSynced }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open || !s) return;
    let alive = true;
    setLoading(true); setError(''); setData(null); setResult(null); setSelected(new Set());
    coaJson(`/scrubber-submissions/${s.id}/sync-targets`)
      .then((d) => {
        if (!alive) return;
        setData(d);
        setSelected(new Set((d.candidates || []).filter((c) => c.selected_default).map((c) => String(c.activity_id))));
      })
      .catch((e) => { if (alive) setError(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, s && s.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const candidates = useMemo(() => (data && data.candidates) || [], [data]);
  const readyCount = useMemo(() => candidates.filter((c) => c.ready).length, [candidates]);
  const dest = (data && data.destination) || { label: 'COA', path: '/app?app=coa' };

  if (!open || !s) return null;

  const toggle = (id) => setSelected((prev) => { const n = new Set(prev); const k = String(id); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const selectAllReady = () => setSelected(new Set(candidates.filter((c) => c.ready).map((c) => String(c.activity_id))));

  async function sync() {
    if (!selected.size) { setError('Pick at least one monitoring row.'); return; }
    setBusy(true); setError('');
    try {
      const d = await coaJson(`/scrubber-submissions/${s.id}/sync`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ activity_ids: [...selected] }) });
      setResult(d);
      if (onSynced) onSynced(d);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  const periodText = s.period && s.period.startDate ? `${s.period.startDate} to ${s.period.endDate}` : null;
  const subtitle = `Submission #${s.id} · ${s.protocol} ${s.tableLabel || ''} · ${s.entityLabel || 'Internal'}${periodText ? ` · ${periodText}` : ''}`;
  const firstSynced = result && (result.results || []).find((r) => r.ok);

  return (
    <Modal open={open} onClose={busy ? undefined : onClose} title={result ? `Synced into ${dest.label}` : afterApproval ? `Accepted. Sync it into ${dest.label}?` : `Sync into ${dest.label}`} wide>
      <p className="text-sm text-slate-500 -mt-2 mb-4">{subtitle}</p>

      {loading && <div className="py-10 text-center text-sm text-slate-500">Looking for the monitoring activity this universe belongs to…</div>}
      {!loading && error && !result && <Notice tone="red">{error}</Notice>}

      {!loading && data && !result && (
        <div className="space-y-4">
          <Notice tone={readyCount > 0 ? 'emerald' : candidates.length ? 'amber' : 'slate'}>
            <span className="text-sm">{data.headline}</span>
          </Notice>

          {candidates.length > 0 && (
            <div>
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="text-xs font-semibold text-slate-600">
                  {candidates.length} matching row{candidates.length === 1 ? '' : 's'} on {dest.label}{data.entity ? ` for ${data.entity}` : ''} · {readyCount} ready
                </div>
                {readyCount > 1 && <button type="button" className={btn.link} onClick={selectAllReady}>Select every ready row</button>}
              </div>
              <div className="space-y-2 max-h-[48vh] overflow-y-auto pr-1">
                {candidates.map((c) => <CandidateRow key={c.activity_id} c={c} snpe={!!data.snpe} checked={selected.has(String(c.activity_id))} onToggle={() => toggle(c.activity_id)} />)}
              </div>
              <div className="text-[11px] text-slate-500 mt-2">
                {data.snpe
                  ? 'An SNPE universe is one file for the year; each month takes its own slice. A month that is not complete for its measure by the engagement date stays unchecked until a later file closes it.'
                  : 'The whole file scores each row it is checked for, exactly as the Sync button on the COA row would, and the COA row records which submission scored it.'}
              </div>
            </div>
          )}

          {candidates.length === 0 && data.reason && (
            <div className="text-xs text-slate-600">
              {data.reason.code === 'no_series' && 'Nothing is lost: the submission stays approved and can be synced later, once the monitoring exists.'}
              {data.reason.code === 'no_entity' && 'If the universe was named for the wrong entity, ask the submitter to resubmit it under the right one.'}
              {data.reason.code === 'no_period' && 'Add the missing periods to the plan, or check the period the submitter declared.'}
            </div>
          )}

          <div className="flex items-center justify-between gap-2 pt-1 flex-wrap">
            <button type="button" className={btn.link} onClick={() => openInCoa(dest, candidates[0] && candidates[0].activity_id)}>Open {dest.label} instead</button>
            <div className="flex gap-2">
              <button type="button" className={btn.secondary} onClick={onClose} disabled={busy}>{readyCount ? 'Not now' : 'Close'}</button>
              {readyCount > 0 && (
                <button type="button" className={btn.primary} onClick={sync} disabled={busy || !selected.size}>
                  {busy ? 'Syncing…' : `Sync ${selected.size || ''} ${selected.size === 1 ? 'row' : 'rows'} into ${dest.label}`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {result && (
        <div className="space-y-4">
          <Notice tone={result.failed ? (result.synced ? 'amber' : 'red') : 'emerald'}>
            <span className="text-sm">
              {result.synced} row{result.synced === 1 ? '' : 's'} scored from submission #{s.id} on {dest.label}{result.failed ? `; ${result.failed} did not sync` : ''}.
            </span>
          </Notice>
          <div className="space-y-2 max-h-[48vh] overflow-y-auto pr-1">
            {(result.results || []).map((r) => (
              <div key={r.activity_id} className={`rounded-xl border px-4 py-3 ${r.ok ? 'border-emerald-200 bg-emerald-50/50' : 'border-rose-200 bg-rose-50/50'}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-sm font-semibold text-slate-800">{r.candidate ? r.candidate.period_label : r.period}<span className="text-slate-400 font-normal"> · {r.candidate ? String(r.candidate.measure_label || '').replace(/^SNPCC\s+—\s+/, '') : ''}</span></div>
                  <Chip tone={r.ok ? 'emerald' : 'rose'}>{r.ok ? 'Synced' : 'Not synced'}</Chip>
                </div>
                <div className="text-xs text-slate-700 mt-1">{r.ok ? r.message : r.error}</div>
                {r.ok && <button type="button" className={`${btn.link} mt-1`} onClick={() => openInCoa(dest, r.activity_id)}>Open this row in {dest.label}</button>}
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-1">
            {firstSynced && <button type="button" className={btn.secondary} onClick={() => openInCoa(dest, firstSynced.activity_id)}>Open {dest.label}</button>}
            <button type="button" className={btn.primary} onClick={onClose}>Done</button>
          </div>
        </div>
      )}
    </Modal>
  );
}
